import { CHOOSE_REPOSITORY, CONNECT_GITHUB, MANAGE_ACCESS, REVIEW_PULL_REQUEST, VIEWER_NOTE } from "../copy/setupCopy";
import { REVIEWS_WORK_WITHOUT_CHECKS } from "../copy/decisionReviewCopy";

function githubStep(setup, repositoryReady) {
  if (!setup?.githubAppEnabled || setup.installationState === "unavailable") {
    return { id: "github", label: "GitHub connected", status: "degraded", detail: "The GitHub App is not available in this environment." };
  }
  if (setup.installationState === "active") {
    return { id: "github", label: "GitHub connected", status: "complete", detail: "The RenderRepo GitHub App is installed for this workspace." };
  }
  if (setup.installationState === "suspended") {
    return { id: "github", label: "GitHub connected", status: "degraded", detail: "The GitHub App installation is suspended." };
  }
  return {
    id: "github",
    label: "GitHub App disconnected",
    status: repositoryReady ? "degraded" : "needs-action",
    detail: repositoryReady
      ? "Public repositories can still be reviewed. Reconnect to review private repositories and publish results."
      : "Connect GitHub before reviewing a private repository.",
  };
}

export function deriveSetupSteps(setup) {
  const repositoryReady = Number(setup?.repositoryCount) > 0;
  const github = githubStep(setup, repositoryReady);
  const repository = repositoryReady
    ? {
        id: "repository",
        label: setup?.installationState === "active" ? "Repository connected" : "Manual reviews available",
        status: "complete",
        detail:
          setup?.installationState === "active"
            ? "Codebases in this workspace can be reviewed."
            : "Public codebases in this workspace can be reviewed without the GitHub App.",
      }
    : {
        id: "repository",
        label: "Codebase authorized",
        status: "needs-action",
        detail: "Choose a codebase this workspace is allowed to review.",
      };
  const pullRequest = repositoryReady
    ? { id: "pull-request", label: "Pull request ready to review", status: "complete", detail: "Paste a pull request URL to review its exact commit." }
    : {
        id: "pull-request",
        label: "Pull request ready to review",
        status: "needs-action",
        detail: "A connected repository is required before a pull request can be reviewed.",
      };
  let publication;
  if (setup?.publicationPossible) {
    publication = { id: "publication", label: "GitHub publication available", status: "complete", detail: "Decision Reviews can be published as a GitHub Check." };
  } else if (repositoryReady && setup?.installationState === "active" && !setup?.checksWrite) {
    publication = { id: "publication", label: "GitHub publication available", status: "degraded", detail: REVIEWS_WORK_WITHOUT_CHECKS };
  } else if (repositoryReady && setup?.checksWrite && setup?.publicationFlagEnabled === false) {
    publication = { id: "publication", label: "GitHub publication available", status: "optional", detail: "Reviews work now. GitHub Check publication is turned off." };
  } else if (setup?.installationState === "suspended") {
    publication = { id: "publication", label: "GitHub publication available", status: "degraded", detail: "Publication waits until the installation is active again." };
  } else {
    publication = { id: "publication", label: "GitHub publication available", status: "optional", detail: "Reviews can start before GitHub Checks are connected." };
  }
  return [github, repository, pullRequest, publication];
}

export function derivePrimaryAction(setup, { canConnect = false } = {}) {
  if (setup?.readOnly) {
    return { id: "view-only", label: VIEWER_NOTE, enabled: false };
  }
  const canReview = setup?.reviewReady === false ? false : Number(setup?.repositoryCount) > 0;
  if (canReview) {
    return { id: "review", label: REVIEW_PULL_REQUEST, enabled: true };
  }
  if (Number(setup?.privateRepositoryCount) > 0) {
    return { id: "connect", label: "Restore private-repository access", enabled: canConnect };
  }
  if (!setup?.githubAppEnabled || setup?.installationState === "unavailable") {
    return { id: "unavailable", label: "GitHub connection is not available in this environment.", enabled: false };
  }
  if (setup?.installationState === "suspended") {
    return { id: "manage", label: MANAGE_ACCESS, enabled: canConnect };
  }
  if (setup?.installationState === "none" || setup?.installationState === "deleted") {
    return { id: "connect", label: CONNECT_GITHUB, enabled: canConnect };
  }
  return { id: "repository", label: CHOOSE_REPOSITORY, enabled: true };
}

export function deriveHealthModel(setup, options) {
  const primary = derivePrimaryAction(setup, options);
  const count = Number(setup?.repositoryCount) || 0;
  if (primary.id === "review") {
    const notes = [`${count} ${count === 1 ? "repository" : "repositories"} available.`];
    if (setup?.installationState === "deleted" || setup?.installationState === "none") {
      notes.push("GitHub App disconnected. Reconnect to review private repositories and publish results on GitHub.");
    }
    notes.push(
      setup?.publicationPossible
        ? "GitHub Checks can publish a finished review."
        : "GitHub publication needs setup. Reviews still run."
    );
    return {
      title: "Ready to review",
      detail: "Paste a pull request and RenderRepo will review its exact commit.",
      primary,
      notes,
    };
  }
  if (primary.id === "connect" && Number(setup?.privateRepositoryCount) > 0) {
    return {
      title: "Restore private-repository access",
      detail: "This workspace only has private repositories, and the GitHub App is not active.",
      primary,
      notes: ["Public repositories can be reviewed without an installation."],
    };
  }
  return {
    title: count ? "Review is not available yet" : "Select a repository",
    detail: primary.label,
    primary,
    notes: [],
  };
}
